import { SKYSPECS_ORG } from 'utils/constants';

/** SkySpecs has no Settings toggle; every other org with a loaded customer can manage it. */
export const canManageDamagesTableNoPreLoading = (customerId?: string | null): boolean =>
  Boolean(customerId) && customerId !== SKYSPECS_ORG;

/**
 * Maps Atlas `tablePreferences.preloading` to the Settings switch /
 * Damages 2.0 defer behavior.
 *
 * API `preloading: true` means load the unfiltered table.
 * UI `shouldDeferUntilFiltered` / "Disable Damages Table Preloading" is the inverse.
 * `null`/`undefined` means no user override; fall back to the organization
 * feature-flag default rather than any leftover localStorage value.
 */
export const toShouldDeferUntilFiltered = ({
  orgFeatureFlagEnabled,
  preloading,
}: {
  orgFeatureFlagEnabled: boolean;
  preloading?: boolean | null;
}): boolean => (typeof preloading === 'boolean' ? !preloading : orgFeatureFlagEnabled);

/** Converts the Settings switch (checked = disable preloading) into the API field. */
export const toTablePreloadingPreference = (shouldDeferUntilFiltered: boolean): boolean =>
  !shouldDeferUntilFiltered;

/**
 * When true, Damages 2.0 asks DataTable to defer the data query until filtered.
 *
 * SkySpecs keeps the hardcoded prod/prod-review behavior and ignores the user toggle.
 * Other orgs defer when No Pre-Loading is on (user preference, else the org flag default).
 *
 * Env asymmetry (intentional): SkySpecs only defers in prod/prod-review so local/stage
 * keep an unfiltered table for debugging. Non-SkySpecs orgs follow the setting in every
 * environment — including local — so empty tables there usually mean No Pre-Loading is on.
 *
 * Column-filter / banner gating lives in DataTable (`deferQueryUntilFiltered`).
 * Callers still short-circuit when Damages-specific universal scope filters are present
 * (site/turbine pages) before passing this boolean.
 */
export const shouldDeferDamages2Query = ({
  customerId,
  isProdLikeEnv,
  shouldDeferUntilFiltered,
}: {
  customerId?: string | null;
  isProdLikeEnv: boolean;
  shouldDeferUntilFiltered: boolean;
}): boolean => {
  // SkySpecs: prod-like only (see env asymmetry note above).
  if (customerId === SKYSPECS_ORG) {
    return isProdLikeEnv;
  }

  return shouldDeferUntilFiltered;
};
